import { Injectable } from '@angular/core';

const PROTOCOL_QUERY_PARAM = 'protocol';
const REF_PROTOCOL_STORAGE_KEY = 'refProtocol';
const REF_PROTOCOL_SAVED_AT_STORAGE_KEY = 'refProtocolSavedAt';
const REF_PROTOCOL_MAX_AGE_DAYS = 90;
const MS_PER_DAY = 24 * 60 * 60 * 1000;


/**
 * @description Reads the marketing `protocol` URL query parameter, persists it in localStorage as `refProtocol`, and purges stale values after the maximum retention window.
 */
@Injectable( {
  providedIn: 'root'
} )
export class IcortexRefProtocolService {


  /**
   * @description Returns the stored ref protocol value, or null when absent or running outside the browser.
   * @return Stored refProtocol value or null.
   */
  getStoredRefProtocol(): string | null {

    if ( typeof window === 'undefined' ) {
      return null;
    }

    return localStorage.getItem( REF_PROTOCOL_STORAGE_KEY );
  }


  /**
   * @description Appends the stored refProtocol value as a `protocol` query parameter to the supplied base URL when available; returns the original URL otherwise.
   * @param baseUrl Destination URL to enrich with the protocol query parameter.
   * @return URL with the protocol query parameter appended or the original URL.
   */
  appendRefProtocolQueryParam( baseUrl: string ): string {

    const protocolValue = this.getStoredRefProtocol();

    if ( !protocolValue ) {
      return baseUrl;
    }

    const separator = baseUrl.includes( '?' ) ? '&' : '?';

    return baseUrl + separator + PROTOCOL_QUERY_PARAM + '=' + encodeURIComponent( protocolValue );
  }


  /**
   * @description Reads `protocol` from the current URL, stores it in localStorage as `refProtocol`, or purges expired stored values when absent.
   */
  syncRefProtocolFromUrl(): void {

    if ( typeof window === 'undefined' ) {
      return;
    }

    const urlParams = new URLSearchParams( window.location.search );
    const protocolValue = urlParams.get( PROTOCOL_QUERY_PARAM );

    if ( protocolValue ) {
      localStorage.setItem( REF_PROTOCOL_STORAGE_KEY, protocolValue );
      localStorage.setItem( REF_PROTOCOL_SAVED_AT_STORAGE_KEY, new Date().toISOString() );

      return;
    }

    this.purgeExpiredRefProtocol();
  }


  /**
   * @description Removes stored ref protocol entries when the capture date is older than the maximum allowed age.
   */
  private purgeExpiredRefProtocol(): void {

    const savedAtRaw = localStorage.getItem( REF_PROTOCOL_SAVED_AT_STORAGE_KEY );

    if ( !savedAtRaw ) {
      return;
    }

    const savedAtTime = Date.parse( savedAtRaw );

    if ( isNaN( savedAtTime ) ) {
      this.removeRefProtocolFromStorage();

      return;
    }

    const ageDays = ( Date.now() - savedAtTime ) / MS_PER_DAY;

    if ( ageDays > REF_PROTOCOL_MAX_AGE_DAYS ) {
      this.removeRefProtocolFromStorage();
    }
  }


  /**
   * @description Clears ref protocol value and capture timestamp from localStorage.
   */
  private removeRefProtocolFromStorage(): void {

    localStorage.removeItem( REF_PROTOCOL_STORAGE_KEY );
    localStorage.removeItem( REF_PROTOCOL_SAVED_AT_STORAGE_KEY );
  }

}
